/**
 * RoutingRuleBuilder Component
 *
 * Visual query builder for creating JSONLogic routing rules.
 * Uses react-querybuilder with custom styling for Meridian Dark design system.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  QueryBuilder,
  type RuleGroupType,
  type CombinatorSelectorProps,
  type FieldSelectorProps,
  type OperatorSelectorProps,
  type ValueEditorProps,
  type Field,
} from 'react-querybuilder';
import { QueryBuilderDndWithoutProvider } from '@react-querybuilder/dnd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import * as ReactDnD from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import * as ReactDndTouchBackend from 'react-dnd-touch-backend';
import 'react-querybuilder/dist/query-builder.css';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  getRoutingFields,
  getRoutingOperators,
  getRoutingCombinators,
} from '../constants/routing-fields';
import {
  queryBuilderToJsonLogic,
  jsonLogicToQueryBuilder,
} from '../utils/jsonlogic-adapter';
import { RoutingRulePreview } from './RoutingRulePreview';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  FieldPickerPopover,
  routingFieldsToSourcesFormat,
  hasCrmFields,
} from '@/components/ui/field-picker';

interface RoutingRuleBuilderProps {
  /** Initial JSONLogic value */
  initialValue?: Record<string, unknown>;
  /** Callback when rule changes */
  onChange: (jsonLogic: Record<string, unknown> | null) => void;
  /** Show JSON preview */
  showPreview?: boolean;
  /** Show raw JSON toggle */
  showRawJson?: boolean;
  /** Compact mode for modals */
  compact?: boolean;
  /** Custom fields (e.g., merged with CRM fields) */
  fields?: Field[];
}

/**
 * Default empty rule group
 */
const defaultQuery: RuleGroupType = {
  combinator: 'and',
  rules: [],
};

/**
 * Custom control elements styled for Meridian Dark
 */
const createControlElements = (fields: Field[], t: TFunction<string>) => ({
  addRuleAction: (props: {
    handleOnClick: () => void;
    disabled?: boolean;
    className?: string;
  }) => (
    <button
      type="button"
      onClick={props.handleOnClick}
      disabled={props.disabled}
      aria-label={t(
        'routing.ruleBuilder.addConditionAria',
        'Add condition to rule group'
      )}
      className={cn(
        'px-3 py-1.5 text-sm font-medium rounded-lg',
        'bg-primary-dim text-primary-fg border border-border',
        'hover:bg-primary-dim transition-colors',
        ...['disabled:opacity-50', 'disabled:cursor-not-allowed'],
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        props.className
      )}
    >
      {t('routing.ruleBuilder.addCondition', '+ Condition')}
    </button>
  ),
  addGroupAction: (props: {
    handleOnClick: () => void;
    disabled?: boolean;
    className?: string;
  }) => (
    <button
      type="button"
      onClick={props.handleOnClick}
      disabled={props.disabled}
      aria-label={t('routing.ruleBuilder.addGroupAria', 'Add nested rule group')}
      className={cn(
        'px-3 py-1.5 text-sm font-medium rounded-lg',
        'bg-bg-subtle text-fg-muted border border-border',
        'hover:bg-bg-muted transition-colors',
        ...['disabled:opacity-50', 'disabled:cursor-not-allowed'],
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        props.className
      )}
    >
      {t('routing.ruleBuilder.addGroup', '+ Group')}
    </button>
  ),
  // Use default dragHandle from react-querybuilder for now
  // Custom styling is applied via controlClassnames.dragHandle
  removeRuleAction: (props: { handleOnClick: () => void; disabled?: boolean }) => (
    <button
      type="button"
      onClick={props.handleOnClick}
      disabled={props.disabled}
      aria-label={t('routing.ruleBuilder.removeConditionAria', 'Remove this condition')}
      className={cn(
        'p-1 text-fg-muted hover:text-danger-fg hover:bg-danger-dim rounded transition-colors',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
      )}
      title={t('routing.ruleBuilder.removeCondition', 'Remove condition')}
    >
      <X className="h-4 w-4" />
    </button>
  ),
  removeGroupAction: (props: { handleOnClick: () => void; disabled?: boolean }) => (
    <button
      type="button"
      onClick={props.handleOnClick}
      disabled={props.disabled}
      aria-label={t('routing.ruleBuilder.removeGroupAria', 'Remove this rule group')}
      className={cn(
        'p-1 text-fg-muted hover:text-danger-fg hover:bg-danger-dim rounded transition-colors',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
      )}
      title={t('routing.ruleBuilder.removeGroup', 'Remove group')}
    >
      <X className="h-4 w-4" />
    </button>
  ),
  combinatorSelector: (props: CombinatorSelectorProps) => {
    // Handle both flat options array and option groups
    type CombinatorOption = { name: string; label: string };
    const flatOptions: CombinatorOption[] = props.options.flatMap((opt) =>
      'options' in opt ? (opt.options as CombinatorOption[]) : [opt as CombinatorOption]
    );
    return (
      <Select
        value={props.value ?? 'and'}
        onValueChange={(val) => props.handleOnChange(val as string)}
        options={flatOptions.map((opt) => ({
          value: opt.name,
          label: opt.label,
        }))}
        size="sm"
        className="w-[80px]"
      />
    );
  },
  fieldSelector: (props: FieldSelectorProps) => {
    // Use multi-source mode if CRM fields present, otherwise simple mode
    const useMultiSource = hasCrmFields(fields);
    const sources = useMultiSource ? routingFieldsToSourcesFormat(fields) : undefined;

    // For simple mode, convert to picker format
    const { pickerFields, pickerGroups } = (() => {
      if (useMultiSource) return { pickerFields: undefined, pickerGroups: undefined };

      const fieldsList = fields.map((field) => ({
        name: field.name,
        label: field.label,
        group: (field.group as string) || 'Other',
        inputType: (field.inputType as 'text' | 'number' | 'select') || 'text',
        values: field.values as Array<{ name: string; label: string }> | undefined,
      }));

      const uniqueGroups = [...new Set(fieldsList.map((f) => f.group))];
      const groupsList = uniqueGroups.map((g, i) => ({
        key: g,
        label: g,
        order: i,
        defaultCollapsed: false,
      }));

      return { pickerFields: fieldsList, pickerGroups: groupsList };
    })();

    return (
      <FieldPickerPopover
        value={(props.value as string) ?? ''}
        onChange={(val: string) => props.handleOnChange(val)}
        sources={sources}
        fields={pickerFields}
        groups={pickerGroups}
        placeholder={t('routing.ruleBuilder.fieldPlaceholder', 'Field...')}
        className="min-w-[140px]"
        width={useMultiSource ? 520 : 400}
      />
    );
  },
  operatorSelector: (props: OperatorSelectorProps) => {
    // Handle both flat options array and option groups
    type OperatorOption = { name: string; label: string };
    const flatOptions: OperatorOption[] = props.options.flatMap((opt) =>
      'options' in opt ? (opt.options as OperatorOption[]) : [opt as OperatorOption]
    );
    return (
      <Select
        value={props.value ?? ''}
        onValueChange={(val) => props.handleOnChange(val as string)}
        options={flatOptions.map((opt) => ({
          value: opt.name,
          label: opt.label,
        }))}
        size="sm"
        className="min-w-[90px]"
      />
    );
  },
  valueEditor: (props: ValueEditorProps) => {
    // Find field definition for additional context
    const fieldDef = fields.find((f) => f.name === props.field);

    // Select input for fields with predefined values (check length, not just existence)
    const hasValues =
      (fieldDef?.values && fieldDef.values.length > 0) ||
      (props.values && props.values.length > 0);

    if (hasValues) {
      const rawOptions = fieldDef?.values || props.values || [];
      const deptPaths = (fieldDef as Record<string, unknown> | undefined)
        ?._departmentPaths as Record<string, string> | undefined;
      // Convert to picker format, filtering out option groups
      const valueFields = rawOptions
        .filter((opt) => !('options' in opt))
        .map((opt) => {
          const optItem = opt as { name: string; label: string };
          return {
            name: optItem.name,
            label: optItem.label,
            group: t('routing.ruleBuilder.valuesGroup', 'Values'),
            inputType: 'select' as const,
            description: deptPaths?.[optItem.name],
          };
        });

      const valueGroups = [
        {
          key: t('routing.ruleBuilder.valuesGroup', 'Values'),
          label: t('routing.ruleBuilder.valuesGroup', 'Values'),
          order: 1,
        },
      ];

      return (
        <FieldPickerPopover
          value={(props.value as string) ?? ''}
          onChange={(val: string) => props.handleOnChange(val)}
          fields={valueFields}
          groups={valueGroups}
          placeholder={t('routing.ruleBuilder.valuePlaceholder', 'Value...')}
          className="min-w-[120px]"
          width={280}
        />
      );
    }

    // Number input with validation
    if (props.inputType === 'number' || fieldDef?.inputType === 'number') {
      // Determine min/max based on field type
      const isScore =
        props.field?.includes('qaScore') || props.field?.includes('sentimentScore');
      const min = props.field?.includes('sentimentScore')
        ? -100
        : isScore
          ? 0
          : undefined;
      const max = isScore ? 100 : undefined;
      const step = isScore ? 1 : undefined;

      // Check if this is an enum field without loaded values (shows ID input)
      const isCrmEnumWithoutValues =
        props.field?.startsWith('crm.') && fieldDef?.label?.endsWith('(ID)');

      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={props.value}
            onChange={(e) => {
              const val = e.target.value;
              // Allow empty for clearing, otherwise validate range
              if (val === '' || (min !== undefined && max !== undefined)) {
                const numVal = Number(val);
                if (val !== '' && min !== undefined && numVal < min) return;
                if (val !== '' && max !== undefined && numVal > max) return;
              }
              props.handleOnChange(val);
            }}
            min={min}
            max={max}
            step={step}
            aria-label={`${t('routing.ruleBuilder.numericValueFor', 'Numeric value for')} ${fieldDef?.label || props.field || t('routing.ruleBuilder.field', 'field')}`}
            aria-valuemin={min}
            aria-valuemax={max}
            placeholder={
              isScore
                ? props.field?.includes('sentimentScore')
                  ? t('routing.ruleBuilder.sentimentRange', '-100 to 100')
                  : t('routing.ruleBuilder.scoreRange', '0-100')
                : isCrmEnumWithoutValues
                  ? t('routing.ruleBuilder.enterId', 'Enter ID')
                  : undefined
            }
            inputSize="sm"
            className={cn('w-24', isCrmEnumWithoutValues && 'border-border')}
          />
          {isCrmEnumWithoutValues && (
            <span
              className="text-xs text-warning"
              title={t(
                'routing.ruleBuilder.enumWithoutValuesHint',
                'This field has enum values. Refresh CRM fields to see options instead of entering IDs.'
              )}
            >
              ⚠️
            </span>
          )}
        </div>
      );
    }

    // Date input for date/datetime fields
    if (props.inputType === 'date' || fieldDef?.inputType === 'date') {
      return (
        <Input
          type="date"
          value={props.value}
          onChange={(e) => props.handleOnChange(e.target.value)}
          aria-label={`${t('routing.ruleBuilder.dateValueFor', 'Date value for')} ${fieldDef?.label || props.field || t('routing.ruleBuilder.field', 'field')}`}
          inputSize="sm"
          className="w-[160px]"
        />
      );
    }

    // Default text input
    return (
      <Input
        type="text"
        value={props.value}
        onChange={(e) => props.handleOnChange(e.target.value)}
        aria-label={`${t('routing.ruleBuilder.textValueFor', 'Text value for')} ${fieldDef?.label || props.field || t('routing.ruleBuilder.field', 'field')}`}
        placeholder={t('routing.ruleBuilder.enterValue', 'Enter value...')}
        inputSize="sm"
        className="w-[160px]"
      />
    );
  },
});

export function RoutingRuleBuilder({
  initialValue,
  onChange,
  showPreview = true,
  showRawJson = false,
  compact = false,
  fields: externalFields,
}: RoutingRuleBuilderProps) {
  // `common` carries the field-group headers (`routing.fieldGroups.*`) and the
  // Yes/No values that `constants/routing-fields.ts` asks this `t` for; with
  // only `settings` declared they fell through to the inline English default.
  const { t } = useTranslation(['settings', 'common']);

  // Get translated fields if none provided externally
  const defaultFields = useMemo(() => getRoutingFields(t), [t]);
  const fields = externalFields ?? defaultFields;

  // Get translated operators and combinators
  const operators = useMemo(() => getRoutingOperators(t), [t]);
  const combinators = useMemo(() => getRoutingCombinators(t), [t]);

  // Initialize query from JSONLogic if provided
  const initialQuery = useMemo(() => {
    if (initialValue) {
      return jsonLogicToQueryBuilder(initialValue);
    }
    return defaultQuery;
  }, [initialValue]);

  const [query, setQuery] = useState<RuleGroupType>(initialQuery);
  const [showRaw, setShowRaw] = useState(showRawJson);

  // Create control elements with current fields and t
  const controlElements = useMemo(() => createControlElements(fields, t), [fields, t]);

  // Convert to JSONLogic and notify parent
  const handleQueryChange = useCallback(
    (newQuery: RuleGroupType) => {
      setQuery(newQuery);
      const jsonLogic = queryBuilderToJsonLogic(newQuery);
      onChange(jsonLogic);
    },
    [onChange]
  );

  // Get current JSONLogic for preview
  const currentJsonLogic = useMemo(() => queryBuilderToJsonLogic(query), [query]);

  return (
    <div className={cn('space-y-4', compact && 'space-y-3')}>
      {/* Query Builder */}
      <div
        className={cn(
          'border border-border rounded-lg p-4 bg-bg-surface',
          compact && 'p-3'
        )}
        data-testid="routing-rule-builder"
      >
        <DndProvider backend={HTML5Backend}>
          <QueryBuilderDndWithoutProvider
            dnd={{ ...ReactDnD, ...ReactDndHtml5Backend, ...ReactDndTouchBackend }}
          >
            <QueryBuilder
              query={query}
              onQueryChange={handleQueryChange}
              fields={fields}
              operators={operators}
              combinators={combinators}
              controlElements={controlElements}
              enableDragAndDrop
              controlClassnames={{
                queryBuilder: 'rqb-queryBuilder w-full',
                ruleGroup:
                  'rqb-ruleGroup p-3 border border-border-muted rounded-lg bg-bg-elevated mb-3',
                header: 'rqb-header flex flex-wrap items-center gap-2 mb-3',
                body: ['rqb-body', 'space-y-2'].join(' '),
                rule: 'rqb-rule flex flex-wrap items-center gap-2 p-2 bg-bg-surface rounded-lg border border-border-muted group',
                combinators: ['rqb-combinators', 'shrink-0'].join(' '),
                addRule: 'rqb-addRule',
                addGroup: 'rqb-addGroup',
                removeGroup: 'rqb-removeGroup',
                removeRule: ['rqb-removeRule', 'ml-auto'].join(' '),
                dragHandle:
                  'rqb-dragHandle cursor-grab active:cursor-grabbing mr-1 opacity-50 hover:opacity-100 select-none',
                fields: 'rqb-fields',
                operators: 'rqb-operators',
                value: 'rqb-value',
              }}
            />
          </QueryBuilderDndWithoutProvider>
        </DndProvider>
      </div>

      {/* Preview Section */}
      {showPreview && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-fg-muted uppercase tracking-wide">
              {t('routing.ruleBuilder.preview', 'Preview')}
            </p>
            <label className="flex items-center gap-2 text-sm text-fg-muted cursor-pointer">
              <Switch checked={showRaw} onCheckedChange={setShowRaw} size="sm" />
              {t('routing.ruleBuilder.showJson', 'Show JSON')}
            </label>
          </div>
          <RoutingRulePreview
            jsonLogic={currentJsonLogic}
            showRaw={showRaw}
            fields={fields}
          />
        </div>
      )}

      {/* Empty state hint */}
      {query.rules.length === 0 && (
        <p className="text-sm text-fg-muted text-center py-4">
          {t(
            'routing.ruleBuilder.emptyHint',
            'Click "+ Add Condition" to start building your routing rule.'
          )}
        </p>
      )}
    </div>
  );
}

export default RoutingRuleBuilder;
